import type { GameState } from '../../types/game-state';
import type { GeographicCoord } from '../../types/world';

// ── Continent outlines ──────────────────────────────────────────────────────
// Each array is [lon, lat] pairs tracing the coastline.
// These are detailed enough to be recognizable at war-room scale.

const NORTH_AMERICA: [number, number][] = [
  // Alaska
  [-168, 65.5], [-166, 64], [-164, 63], [-161, 63], [-158, 62],
  [-155, 60], [-153, 59], [-152, 60], [-150, 61], [-147, 61.5],
  [-142, 60], [-140, 60], [-139, 59.5],
  // BC / Pacific coast
  [-137, 58.5], [-136, 57], [-135, 56.5], [-133, 55], [-131, 54],
  [-130, 53], [-129, 52], [-127, 51], [-126, 50], [-125, 49],
  [-124.5, 48], [-124, 46], [-124, 44], [-124, 42],
  // California
  [-122.5, 40], [-121, 38], [-120, 36], [-118.5, 34], [-117.5, 33],
  [-117, 32.5],
  // Mexico Pacific
  [-115, 30], [-112, 28], [-110, 25], [-108, 23.5], [-106, 22],
  [-105, 20.5], [-104, 19], [-102, 18], [-100, 17], [-98, 16.5],
  [-96, 16], [-94, 16.5], [-92, 15.5],
  // Central America
  [-90, 14.5], [-88, 14], [-86, 13], [-84, 11], [-83, 10],
  [-82, 9], [-80, 8],
  // Caribbean coast
  [-77, 8.5], [-76, 9], [-75, 10.5],
  // Mexico Gulf / Gulf coast
  [-82, 10], [-84, 16], [-87, 18], [-88, 21], [-90, 21.5],
  [-91, 19], [-93, 18.5], [-95, 19.5], [-97, 22], [-97, 26],
  [-96, 28], [-94, 29.5], [-92, 29.5], [-90, 29], [-89, 29.5],
  [-88.5, 30], [-87, 30.5], [-85.5, 30],
  // Florida
  [-84, 30], [-82, 29], [-81, 27], [-80, 25.5], [-81, 25],
  [-82, 27], [-83, 28.5], [-84, 29.5],
  // US East coast
  [-81, 31], [-79, 33], [-77, 35], [-76, 36.5], [-75.5, 37.5],
  [-74, 39.5], [-73, 40.5], [-72, 41], [-71, 41.5], [-70, 42],
  [-69.5, 43.5], [-67, 44.5], [-66.5, 45.5],
  // Maritime Canada
  [-66, 44], [-63, 44.5], [-61, 46], [-60, 47], [-59, 47.5],
  [-57, 47], [-56, 47.5], [-55, 49], [-57, 50], [-58, 51],
  [-56, 52], [-55, 52.5],
  // Labrador
  [-57, 53], [-59, 54], [-60, 55], [-59, 56], [-62, 58],
  [-64, 60], [-66, 61], [-69, 62.5], [-71, 63],
  // Hudson Bay east
  [-73, 62], [-77, 62], [-79, 61], [-80, 60], [-82, 59],
  [-84, 58.5], [-86, 58],
  // Hudson Bay west
  [-87, 57], [-88, 56], [-89, 55.5], [-92, 57], [-94, 58],
  [-95, 59], [-94, 60], [-93, 61],
  // Canadian Arctic
  [-91, 63], [-86, 65], [-83, 66], [-80, 67], [-78, 69],
  [-76, 72], [-73, 73], [-71, 71], [-68, 70], [-65, 69.5],
  [-62, 70], [-60, 72], [-63, 74], [-68, 76], [-73, 76],
  [-80, 76], [-85, 74], [-90, 74], [-95, 73], [-100, 73],
  [-105, 72], [-110, 72], [-115, 71], [-120, 71], [-125, 72],
  [-130, 72], [-135, 70],
  // NW Canada / Alaska north
  [-138, 69], [-141, 69.5], [-145, 70], [-148, 70.5],
  [-152, 71], [-156, 71.5], [-160, 71], [-163, 70],
  [-166, 69], [-168, 67], [-168, 65.5],
];

const SOUTH_AMERICA: [number, number][] = [
  // North coast (Venezuela / Guyana)
  [-77, 8], [-74, 11], [-72, 12], [-70, 12.5], [-68, 12],
  [-66, 10.5], [-64, 10.5], [-62, 11], [-60, 10.5], [-58, 8],
  [-56, 6], [-54, 5], [-52, 4], [-51, 3],
  // Brazil north coast
  [-50, 1], [-49, 0], [-48, -1], [-46, -1.5], [-44, -2.5],
  [-42, -3], [-39, -4], [-37, -5], [-35, -6.5],
  // Brazil east coast
  [-35, -8], [-34.5, -10], [-35.5, -12], [-37, -13],
  [-38.5, -14.5], [-39, -16], [-39.5, -18], [-40, -20],
  [-41, -21], [-41, -22.5], [-43, -23],
  // Brazil south / Uruguay
  [-44, -23.5], [-46, -24], [-48, -26], [-48.5, -28],
  [-50, -29], [-51, -30], [-52, -32], [-53, -33.5],
  // Argentina
  [-55, -34], [-56.5, -35], [-58, -36], [-58.5, -38],
  [-60, -39], [-62, -40.5], [-63, -42], [-65, -43.5],
  [-65.5, -45], [-67, -46], [-66, -48.5], [-65.5, -50],
  [-68, -50.5], [-69, -52], [-70, -53.5], [-69, -55],
  // Tierra del Fuego / Cape Horn
  [-68, -54.5], [-66, -55], [-67, -56],
  // Chile
  [-71, -53], [-73, -51], [-75, -48], [-74, -45],
  [-73, -43], [-72.5, -41], [-73, -39], [-73.5, -37],
  [-72, -35], [-71.5, -33], [-71, -30],
  // Peru / Ecuador / Colombia
  [-70.5, -27], [-70, -24], [-70, -21], [-70, -18],
  [-71, -16], [-73, -15], [-75, -14], [-76, -12],
  [-77, -10], [-78, -7], [-79, -5], [-80.5, -3],
  [-80, -1], [-80, 1], [-78, 3], [-77, 5], [-77, 8],
];

const EUROPE: [number, number][] = [
  // Iberia
  [-9, 37], [-9.5, 39], [-8, 40], [-9, 41], [-8.5, 43],
  [-5, 43.5], [-3, 43.5], [-2, 43], [0, 43],
  // France Mediterranean
  [3, 43], [4, 43.5], [5, 43.5], [6.5, 43],
  // Italy
  [8, 44], [9, 44], [10, 43], [11, 42.5], [12.5, 42],
  [13, 41], [14, 41], [15, 40], [16, 39], [15.5, 38],
  [16, 37.5], [15, 37],
  // Sicily / heel
  [13, 38], [12, 38], [12.5, 37.5], [15, 36.5],
  // Back to mainland
  [14, 40.5], [13, 42], [12, 44], [13.5, 45.5],
  // Adriatic east / Balkans
  [14, 45], [15, 44.5], [16, 43.5], [17, 43], [18, 42.5],
  [19, 42], [20, 40], [20, 38], [21, 37.5], [22, 37],
  [23, 36], [24, 35.5], [25, 35],
  // Greece / Turkey
  [26, 36], [27, 37], [28, 36.5], [29, 37], [26, 38],
  [26.5, 39], [25, 40], [24, 40.5], [23.5, 41],
  // Bulgaria / Romania / Black Sea
  [26, 42], [28, 43], [29, 44], [30, 45], [30, 46.5],
  // Ukraine / Poland
  [29, 48], [27, 49], [24, 50], [22, 49.5], [20, 49.5],
  [18, 50], [17, 51], [15, 51], [14, 52], [14, 53.5],
  // Baltic
  [13.5, 54.5], [14, 55], [16, 55], [17, 55.5],
  [18, 56], [18, 57.5], [20, 58], [20, 59], [22, 59.5],
  [24, 58], [25, 59], [24, 60], [23, 60.5],
  // Finland
  [25, 60.5], [27, 61], [28, 63], [27, 65], [25.5, 66],
  [24, 68], [26, 69.5], [28, 70], [30, 70], [31, 70.5],
  // Norway (north to south)
  [28, 71], [25, 71], [22, 70], [18, 69.5], [16, 69],
  [14, 68], [13, 66], [12, 65], [11, 63], [10, 62],
  [7, 62.5], [5, 61.5], [5, 60], [6, 58], [8, 57.5],
  [10, 56], [12, 55.5],
  // Denmark / Germany / Netherlands
  [10, 55], [9, 54], [8.5, 53.5], [7, 53.5], [5, 52],
  [4, 51.5], [3, 51],
  // France Atlantic
  [2, 51], [1.5, 50.5], [0, 49.5], [-1, 49], [-2, 48.5],
  [-3, 48.5], [-4, 48], [-5, 48], [-4, 47], [-3, 47],
  [-1.5, 46.5], [-1, 45.5], [-1, 44], [-1.5, 43],
  [-2, 43], [-5, 43.5], [-8.5, 43], [-9, 41], [-9.5, 39],
  [-9, 37],
];

const AFRICA: [number, number][] = [
  // Morocco / NW Africa
  [-13, 28], [-15, 25], [-16, 22], [-17, 20], [-16, 18],
  [-16, 15], [-15, 12], [-13, 12], [-12, 10], [-13, 8],
  // West Africa coast
  [-15, 11], [-16.5, 13], [-17, 14.5], [-16, 16], [-16, 18],
  [-15, 12], [-12, 8], [-11, 6], [-10, 7], [-8, 5.5],
  [-7, 4.5], [-5, 5], [-4, 5], [-3, 5], [-1, 5.5],
  [1, 6], [2.5, 6.5],
  // Gulf of Guinea
  [4, 6], [5, 5], [5, 4], [7, 4.5], [8, 4.5],
  [9, 4], [9.5, 3], [10, 2], [9.5, 1], [9, 1],
  [9, 0], [10, -1], [11, -3], [12, -5.5],
  // SW Africa
  [12, -8], [13, -10], [12, -13], [12, -17],
  [14, -20], [15, -22], [14.5, -25], [15.5, -27],
  [16.5, -28.5], [17.5, -30], [18, -32], [18.5, -34],
  // South Africa
  [20, -34.5], [22, -34], [25, -33.5], [27, -33],
  [28.5, -32], [29, -30], [30, -29], [32, -27],
  [32.5, -26], [33, -25],
  // Mozambique / Tanzania / East Africa
  [35, -23], [36, -20], [35.5, -18], [35, -15],
  [40, -11], [39.5, -8], [39, -5], [41, -2],
  [42, 0], [44, 2], [46, 4], [48, 8], [49, 10], [50, 12],
  // Horn of Africa
  [51, 11], [49, 9], [46, 8], [44, 10], [43, 11.5],
  // Red Sea coast
  [43, 13], [42, 15], [41, 16], [40, 17.5],
  [39, 18], [37, 19], [36, 20],
  // Egypt / Libya / NW
  [33, 22], [32, 24], [32, 28], [33, 30], [32, 31.5],
  [30, 31.5], [27, 31.5], [25, 31.5],
  [20, 32], [15, 32], [12, 33], [10, 34], [8, 37],
  [5, 37], [2, 35.5], [0, 35.5], [-1, 35], [-3, 35.5],
  [-5, 35.5], [-6, 35], [-8, 35.5], [-10, 35],
  [-12, 34], [-13, 28],
];

const ASIA_MAINLAND: [number, number][] = [
  // Turkey / Middle East
  [26, 36], [28, 36.5], [30, 37], [33, 36], [36, 36.5],
  [36, 35.5], [35, 33], [35, 32], [34.5, 31],
  // Levant / Arabian Peninsula
  [34, 29.5], [35, 28], [36, 26], [37, 24], [39, 22],
  [41, 20], [43, 17], [44, 15], [45, 13], [48, 12.5],
  [51, 12.5], [53, 16.5], [55, 17], [56, 20],
  [57, 22], [57, 24], [55, 25.5], [52, 26], [50, 27],
  [50, 29.5], [48, 30],
  // Iran / Pakistan
  [49, 32], [51, 35], [53, 37], [55, 38], [57, 37.5],
  [60, 36], [62, 35], [63, 34], [64, 32], [66, 26],
  [67, 24.5], [68, 24],
  // India west coast
  [68, 23], [70, 21], [72, 20], [73, 18], [73, 16],
  [74, 14], [75, 12], [76, 10], [77, 8.5],
  // Sri Lanka (skip) / India east coast
  [78, 8], [79, 10], [80, 13], [80, 15], [82, 17],
  [83, 18], [85, 20], [87, 21.5], [89, 22],
  // Bangladesh / Myanmar / Thailand
  [90, 22.5], [92, 21], [93, 20], [94, 19], [95, 17],
  [97, 16.5], [98, 14], [99, 12], [100, 10], [100, 7],
  [101, 5], [103, 3], [104, 1.5],
  // Malay Peninsula
  [104, 1], [103, 2], [101, 3.5], [100, 5], [100, 7],
  // Vietnam / South China
  [104, 2], [106, 6], [107, 10], [108, 12], [109, 14],
  [109, 16], [108, 18], [107, 20], [106, 21], [107, 22],
  // China south coast
  [109, 22], [110, 20], [112, 22], [114, 23], [116, 23.5],
  [118, 24.5], [120, 23], [121, 25], [120, 27], [122, 30],
  [121, 31], [121, 33], [120, 34], [119, 35],
  // China east coast / Korea
  [120, 36], [122, 37], [124, 38], [126, 35], [127, 34.5],
  [129, 36], [129, 38], [128, 39], [127, 40],
  [126, 39.5], [125, 40], [124, 40], [122, 41],
  // Manchuria / Russian Pacific
  [121, 43], [123, 45], [127, 43], [130, 43], [131, 44],
  [133, 45], [134, 48], [136, 49], [137, 50],
  [139, 51.5], [141, 53], [140, 55], [137, 57],
  [135, 59], [133, 60], [131, 60.5],
  // Kamchatka / Okhotsk
  [130, 62], [132, 64], [135, 65], [138, 64],
  [140, 62], [143, 59], [145, 57], [148, 55],
  [150, 53], [152, 53], [155, 55], [156, 57],
  [157, 59], [160, 60], [162, 62], [163, 64],
  [170, 65], [172, 64],
  // Back along Russia north
  [175, 65], [177, 67], [179, 68],
  // Russia Arctic coast (east to west)
  [179, 70], [175, 72], [170, 71], [165, 70], [160, 72],
  [155, 72], [150, 72], [145, 73], [140, 74],
  [130, 73.5], [120, 73], [110, 73.5], [100, 74],
  [90, 73], [80, 73], [75, 72], [70, 70.5],
  [65, 68], [60, 69.5], [55, 70], [50, 70], [45, 68.5],
  [40, 67], [38, 65.5],
  // Through Scandinavia/Ural back to Black Sea
  [36, 62], [38, 60], [40, 58], [38, 56],
  [36, 55], [35, 53], [34, 52], [32, 50.5],
  [31, 48], [30, 46.5], [30, 45], [29, 44],
  [28, 43], [26, 42], [24, 40.5], [23.5, 41],
  [25, 40], [26.5, 39], [26, 38], [27, 37],
  [26, 36],
];

const JAPAN: [number, number][] = [
  [130, 31], [131, 33], [131.5, 34], [132, 35],
  [134, 34.5], [135, 35.5], [136, 36.5], [137, 37],
  [139, 37.5], [140, 38.5], [140, 40], [140, 41.5],
  [141, 43], [142, 44], [145, 44], [145, 43],
  [143, 42], [142, 41], [141, 39], [140, 38],
  [140, 36], [139, 35], [137, 34], [135, 34],
  [133, 33], [131, 32], [130, 31],
];

const AUSTRALIA: [number, number][] = [
  [115, -14], [117, -14], [119, -14.5], [121, -14],
  [124, -13], [127, -12.5], [130, -12], [133, -12],
  [135, -12.5], [136, -13.5], [136, -14.5], [137, -16],
  [138, -17], [140, -17.5], [141, -18], [142, -19],
  [144, -20], [146, -19], [146, -20], [148, -20.5],
  [149, -22], [150, -24], [151, -25], [152, -26],
  [153, -27.5], [153, -29], [153, -30], [152, -32],
  [151, -33.5], [150, -35], [148, -37], [146, -38.5],
  [145, -38.5], [143, -38.5], [141, -38], [139, -36.5],
  [137, -35.5], [136, -35], [134, -33], [132, -32],
  [130, -31.5], [128, -31.5], [126, -32], [124, -33],
  [122, -34], [119, -34], [116, -34], [114.5, -33],
  [114, -31], [113.5, -28], [113, -25], [114, -22],
  [114, -20], [114.5, -18], [115, -15], [115, -14],
];

const UK_IRELAND: [number, number][] = [
  // Ireland
  [-10, 51.5], [-9.5, 52], [-10, 53], [-10, 54],
  [-8, 55.5], [-6.5, 55], [-5.5, 54.5], [-6, 53],
  [-6, 52], [-7, 52], [-9, 51.5], [-10, 51.5],
];

const GREAT_BRITAIN: [number, number][] = [
  [-5, 50], [-3, 50.5], [0, 51], [1.5, 51.5],
  [1.5, 52.5], [0, 53], [0, 54], [-1, 55],
  [-2, 56], [-3, 56.5], [-4, 57.5], [-5, 58],
  [-5.5, 58.5], [-4.5, 57], [-6, 56], [-5.5, 55.5],
  [-4, 54.5], [-3.5, 54], [-3, 53.5], [-4, 53],
  [-5, 52], [-5, 51], [-5, 50],
];

const INDONESIA: [number, number][] = [
  // Sumatra
  [95, 5.5], [98, 4], [100, 2], [104, -1], [106, -2.5],
  [105, -5.5], [103, -5], [100, -3.5], [98, -1],
  [96, 2], [95, 5.5],
];

const BORNEO: [number, number][] = [
  [109, 1], [110, 2], [112, 3], [114, 4.5], [116, 5],
  [118, 6.5], [119, 5], [118, 3], [117, 1.5],
  [116, 0.5], [115, -1], [114, -2.5], [112, -3],
  [111, -2.5], [110, -1], [109, 0], [109, 1],
];

const NEW_ZEALAND: [number, number][] = [
  [173, -35], [174, -37], [176, -38], [178, -39],
  [178, -41], [177, -42], [175, -42], [173, -41],
  [171, -44], [169, -46], [167, -46], [166, -45],
  [168, -44], [170, -43], [171, -42], [172, -41],
  [172, -39], [173, -38], [173, -35],
];

const ALL_CONTINENTS: [number, number][][] = [
  NORTH_AMERICA,
  SOUTH_AMERICA,
  EUROPE,
  AFRICA,
  ASIA_MAINLAND,
  JAPAN,
  AUSTRALIA,
  UK_IRELAND,
  GREAT_BRITAIN,
  INDONESIA,
  BORNEO,
  NEW_ZEALAND,
];

// ── Map rendering ───────────────────────────────────────────────────────────

interface MapConfig {
  readonly canvas: HTMLCanvasElement;
  readonly primaryColor: string;
  readonly dimColor: string;
  readonly dangerColor: string;
  readonly warningColor: string;
}

function projectToCanvas(
  coord: GeographicCoord,
  width: number,
  height: number,
  padding: number = 20,
): { x: number; y: number } {
  const usableWidth = width - padding * 2;
  const usableHeight = height - padding * 2;
  return {
    x: padding + ((coord.lon + 180) / 360) * usableWidth,
    y: padding + ((90 - coord.lat) / 180) * usableHeight,
  };
}

function drawMap(config: MapConfig, state: GameState): void {
  const { canvas } = config;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const dpr = window.devicePixelRatio || 1;
  const width = canvas.width / dpr;
  const height = canvas.height / dpr;

  // Clear
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = '#0a0a0a';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.restore();

  // Grid lines
  ctx.strokeStyle = 'rgba(51, 255, 51, 0.06)';
  ctx.lineWidth = 0.5;
  for (let lon = -180; lon <= 180; lon += 30) {
    const { x } = projectToCanvas({ lat: 0, lon }, width, height);
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, height);
    ctx.stroke();
  }
  for (let lat = -90; lat <= 90; lat += 30) {
    const { y } = projectToCanvas({ lat, lon: 0 }, width, height);
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();
  }

  // Draw continents
  ctx.strokeStyle = config.dimColor;
  ctx.lineWidth = 1.2;
  for (const continent of ALL_CONTINENTS) {
    ctx.beginPath();
    const first = continent[0];
    if (!first) continue;
    const start = projectToCanvas({ lat: first[1], lon: first[0] }, width, height);
    ctx.moveTo(start.x, start.y);
    for (let i = 1; i < continent.length; i++) {
      const point = continent[i];
      if (!point) continue;
      const { x, y } = projectToCanvas({ lat: point[1], lon: point[0] }, width, height);
      ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.stroke();

    // Subtle fill
    ctx.fillStyle = 'rgba(51, 255, 51, 0.03)';
    ctx.fill();
  }

  // Draw cities
  const allCities = [...state.world.usCities, ...state.world.ussrCities];
  for (const city of allCities) {
    const { x, y } = projectToCanvas(city.coord, width, height);
    if (city.isDestroyed) {
      // Destroyed: red X
      ctx.strokeStyle = config.dangerColor;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x - 4, y - 4);
      ctx.lineTo(x + 4, y + 4);
      ctx.moveTo(x + 4, y - 4);
      ctx.lineTo(x - 4, y + 4);
      ctx.stroke();

      // Red glow
      ctx.fillStyle = 'rgba(255, 51, 51, 0.15)';
      ctx.beginPath();
      ctx.arc(x, y, 8, 0, Math.PI * 2);
      ctx.fill();
    } else {
      const color = city.country === 'US' ? config.primaryColor : config.warningColor;

      // City dot
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fill();

      // Label for large cities
      if (city.currentPopulation > 2_000_000) {
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.6;
        ctx.font = '8px Courier New';
        ctx.fillText(city.name, x + 5, y + 3);
        ctx.globalAlpha = 1;
      }
    }
  }

  // Draw military installations
  const allInstallations = [...state.world.usInstallations, ...state.world.ussrInstallations];
  for (const inst of allInstallations) {
    const { x, y } = projectToCanvas(inst.coord, width, height);
    if (inst.isDestroyed) {
      ctx.strokeStyle = config.dangerColor;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(x - 3, y - 3);
      ctx.lineTo(x + 3, y + 3);
      ctx.moveTo(x + 3, y - 3);
      ctx.lineTo(x - 3, y + 3);
      ctx.stroke();
    } else {
      const color = inst.country === 'US' ? 'rgba(51, 255, 51, 0.6)' : 'rgba(255, 204, 51, 0.6)';
      ctx.fillStyle = color;
      // Triangle for silos, diamond for command, square for others
      if (inst.type === 'icbm_silo') {
        ctx.beginPath();
        ctx.moveTo(x, y - 4);
        ctx.lineTo(x - 3, y + 3);
        ctx.lineTo(x + 3, y + 3);
        ctx.closePath();
        ctx.fill();
      } else if (inst.type === 'command_center') {
        ctx.beginPath();
        ctx.moveTo(x, y - 4);
        ctx.lineTo(x + 4, y);
        ctx.lineTo(x, y + 4);
        ctx.lineTo(x - 4, y);
        ctx.closePath();
        ctx.fill();
      } else {
        ctx.fillRect(x - 2.5, y - 2.5, 5, 5);
      }
    }
  }
}

// ── Missile animation ───────────────────────────────────────────────────────

interface MissileArc {
  readonly from: GeographicCoord;
  readonly to: GeographicCoord;
  readonly color: string;
  progress: number;
}

// Animation speed: 0.0075 per frame (~67 frames to complete = ~4.5s at 60fps)
const MISSILE_SPEED = 0.0075;
const ARC_STEPS = 80;

function animateMissiles(
  config: MapConfig,
  state: GameState,
  arcs: MissileArc[],
  onComplete: () => void,
): void {
  const { canvas } = config;
  const maybeCtx = canvas.getContext('2d');
  if (!maybeCtx) return;
  const ctx: CanvasRenderingContext2D = maybeCtx;

  const dpr = window.devicePixelRatio || 1;
  const width = canvas.width / dpr;
  const height = canvas.height / dpr;

  let animating = true;
  // Hold on detonation for a beat before calling complete
  let detonationHoldFrames = 0;
  const DETONATION_HOLD = 90; // ~1.5s at 60fps

  function frame(): void {
    if (!animating) return;

    // Redraw base map
    drawMap(config, state);

    let allDone = true;

    for (const arc of arcs) {
      if (arc.progress < 1) {
        allDone = false;
        arc.progress = Math.min(1, arc.progress + MISSILE_SPEED);
      }

      const from = projectToCanvas(arc.from, width, height);
      const to = projectToCanvas(arc.to, width, height);

      // Draw trail
      const steps = Math.floor(arc.progress * ARC_STEPS);
      ctx.strokeStyle = arc.color;
      ctx.lineWidth = 1.5;

      // Fading trail segments
      for (let i = 1; i <= steps; i++) {
        const t0 = (i - 1) / ARC_STEPS;
        const t1 = i / ARC_STEPS;

        const x0 = from.x + (to.x - from.x) * t0;
        const y0 = from.y + (to.y - from.y) * t0 - Math.sin(t0 * Math.PI) * 50;
        const x1 = from.x + (to.x - from.x) * t1;
        const y1 = from.y + (to.y - from.y) * t1 - Math.sin(t1 * Math.PI) * 50;

        // Fade: older segments are dimmer
        const age = (arc.progress * ARC_STEPS - i) / (ARC_STEPS * 0.3);
        ctx.globalAlpha = Math.max(0.15, 1 - age * 0.6);
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;

      // Warhead head
      if (arc.progress < 1) {
        const t = arc.progress;
        const hx = from.x + (to.x - from.x) * t;
        const hy = from.y + (to.y - from.y) * t - Math.sin(t * Math.PI) * 50;

        // Bright core
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(hx, hy, 2.5, 0, Math.PI * 2);
        ctx.fill();

        // Color glow
        ctx.fillStyle = arc.color;
        ctx.globalAlpha = 0.4;
        ctx.beginPath();
        ctx.arc(hx, hy, 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      } else {
        // Detonation effect: expanding rings
        const { x, y } = projectToCanvas(arc.to, width, height);
        const pulsePhase = (detonationHoldFrames % 30) / 30;

        // Inner flash
        ctx.fillStyle = '#ff5555';
        ctx.globalAlpha = 0.7;
        ctx.beginPath();
        ctx.arc(x, y, 6 + pulsePhase * 4, 0, Math.PI * 2);
        ctx.fill();

        // Outer ring
        ctx.strokeStyle = '#ff3333';
        ctx.lineWidth = 1;
        ctx.globalAlpha = 0.3;
        ctx.beginPath();
        ctx.arc(x, y, 10 + pulsePhase * 8, 0, Math.PI * 2);
        ctx.stroke();

        ctx.globalAlpha = 1;
      }
    }

    if (allDone) {
      detonationHoldFrames++;
      if (detonationHoldFrames >= DETONATION_HOLD) {
        animating = false;
        onComplete();
        return;
      }
    }

    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);
}

// ── Fullscreen map management ───────────────────────────────────────────────

function showMap(canvas: HTMLCanvasElement): void {
  const dpr = window.devicePixelRatio || 1;
  const w = window.innerWidth;
  const h = window.innerHeight;

  // Set canvas resolution to full viewport for crisp rendering
  canvas.width = w * dpr;
  canvas.height = h * dpr;

  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.scale(dpr, dpr);
  }

  canvas.classList.add('visible');

  // Hide terminal text while map is fullscreen
  const terminal = document.getElementById('terminal');
  if (terminal) {
    terminal.classList.add('map-active');
  }
}

function hideMap(canvas: HTMLCanvasElement): void {
  canvas.classList.remove('visible');

  const terminal = document.getElementById('terminal');
  if (terminal) {
    terminal.classList.remove('map-active');
  }
}

export { drawMap, animateMissiles, showMap, hideMap, projectToCanvas };
export type { MapConfig, MissileArc };
